import { RewardWalletPriority, IUserWalletRewardItem } from '@share/common-types';

const AVAILABLE_STATUS = 'Available';

export function getNextRewardByPriority(
  allRewards: IUserWalletRewardItem[] | undefined,
  priority: RewardWalletPriority | undefined,
): IUserWalletRewardItem | undefined {
  const withValue = (allRewards ?? []).filter(
    (r) => r.status === AVAILABLE_STATUS && r.rewardValue != null && r.rewardValue > 0,
  );
  if (withValue.length === 0) return undefined;
  if (priority === RewardWalletPriority.Highest) {
    return withValue.reduce((best, r) => ((r.rewardValue ?? 0) > (best.rewardValue ?? 0) ? r : best));
  }
  if (priority === RewardWalletPriority.Cheapest) {
    return withValue.reduce((best, r) => ((r.rewardValue ?? 0) < (best.rewardValue ?? 0) ? r : best));
  }
  return withValue[0];
}

/**
 * Select reward by priority: Highest, Cheapest, or Best.
 * For Best, uses maxRewardCash to pick the reward that best fits (exact match, else closest from above, else largest below).
 */
export function getRewardByPriority(
  allRewards: IUserWalletRewardItem[] | undefined,
  priority: RewardWalletPriority | undefined,
  maxRewardCash?: number,
): IUserWalletRewardItem | undefined {
  if (priority === RewardWalletPriority.Best && maxRewardCash != null) {
    return getBestRewardByMaxRewardCash(allRewards, maxRewardCash);
  }
  return getNextRewardByPriority(allRewards, priority);
}

/**
 * Select the "best" reward: the one whose rewardValue is closest to maxRewardCash.
 * - If any reward has rewardValue === maxRewardCash, select that one.
 * - If there are values > maxRewardCash, select the smallest among those (closest from above).
 * - If all values < maxRewardCash, select the one with the biggest rewardValue.
 */
export function getBestRewardByMaxRewardCash(
  allRewards: IUserWalletRewardItem[] | undefined,
  maxRewardCash: number,
): IUserWalletRewardItem | undefined {
  const withValue = (allRewards ?? []).filter(
    (r) => r.status === AVAILABLE_STATUS && r.rewardValue != null && r.rewardValue > 0,
  );
  if (withValue.length === 0) return undefined;

  const exactMatch = withValue.find((r) => (r.rewardValue ?? 0) === maxRewardCash);
  if (exactMatch) return exactMatch;

  const greaterOrEqual = withValue.filter((r) => (r.rewardValue ?? 0) >= maxRewardCash);
  if (greaterOrEqual.length > 0) {
    return greaterOrEqual.reduce((best, r) =>
      (r.rewardValue ?? 0) < (best.rewardValue ?? 0) ? r : best,
    );
  }

  return withValue.reduce((best, r) =>
    (r.rewardValue ?? 0) > (best.rewardValue ?? 0) ? r : best,
  );
}
